// Password and passphrase generator. Uniform randomness via rejection sampling
// (no modulo bias). Entropy follows the design: length × log2(charset), where
// the charset is 25 lowercase (no l) + 24 uppercase (no I/O) + 8 digits (2–9)
// + 13 symbols.

import { WORDS } from "./words";

export interface GenOptions {
  mode: "pw" | "phrase";
  len: number;
  upper: boolean;
  num: boolean;
  sym: boolean;
  words: number;
}

export const DEFAULT_GEN: GenOptions = { mode: "pw", len: 20, upper: true, num: true, sym: true, words: 5 };

const LOWER = "abcdefghijkmnopqrstuvwxyz";
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const DIGITS = "23456789";
const SYMBOLS = "!@#$%^&*-_=+?";

/** Uniform random integer in [0, n). */
export function randomInt(n: number): number {
  const limit = Math.floor(0x1_0000_0000 / n) * n;
  const buf = new Uint32Array(1);
  do crypto.getRandomValues(buf);
  while (buf[0]! >= limit);
  return buf[0]! % n;
}

export function charset(o: GenOptions): string {
  return LOWER + (o.upper ? UPPER : "") + (o.num ? DIGITS : "") + (o.sym ? SYMBOLS : "");
}

export function generate(o: GenOptions): string {
  if (o.mode === "phrase") return passphrase(o.words);
  const set = charset(o);
  let out = "";
  for (let i = 0; i < o.len; i++) out += set[randomInt(set.length)];
  return out;
}

/** Words joined by hyphens, plus a two-digit number, e.g. ridge-copper-orbit-42. */
export function passphrase(words: number, withNumber = true): string {
  const parts: string[] = [];
  for (let i = 0; i < words; i++) parts.push(WORDS[randomInt(WORDS.length)]!);
  return parts.join("-") + (withNumber ? `-${randomInt(90) + 10}` : "");
}

export function entropyBits(o: GenOptions): number {
  if (o.mode === "phrase") return Math.round(o.words * Math.log2(WORDS.length) + Math.log2(90));
  return Math.round(o.len * Math.log2(charset(o).length));
}

export function strength(bits: number): { label: string; tone: "success" | "warning" | "danger" } {
  if (bits >= 100) return { label: "Very strong", tone: "success" };
  if (bits >= 70) return { label: "Strong", tone: "success" };
  if (bits >= 50) return { label: "Fair", tone: "warning" };
  return { label: "Weak", tone: "danger" };
}
